import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Truck, CheckCircle2, MapPin } from 'lucide-react';

/**
 * Modal for the prize winner to submit their shipping address.
 * Triggers backend POST /api/escrow/{id}/shipping which generates an order# and emails both parties.
 *
 * Props: escrow (object), open (bool), onOpenChange(fn), onSubmitted(orderNumber) callback
 */
export function ShippingInfoDialog({ escrow, open, onOpenChange, onSubmitted, prefillEmail }) {
  const [form, setForm] = useState({
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
    email: prefillEmail || '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null); // {order_number, ship_deadline}

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    const required = ['full_name', 'address_line1', 'city', 'postal_code', 'country'];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) {
        toast.error(`${k.replace('_', ' ')} is required`);
        return;
      }
    }
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error('Valid email required for order confirmation');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/escrow/${escrow.escrow_id}/shipping`, form);
      // Show success FIRST — don't call onSubmitted yet; that fires after user dismisses.
      setSuccess({ order_number: res.data.order_number, ship_deadline: res.data.ship_deadline });
      toast.success('Shipping info submitted!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  const handleClose = () => {
    const wasSuccess = !!success;
    const orderNumber = success?.order_number;
    setSuccess(null);
    onOpenChange(false);
    // Notify parent AFTER close so parent's fetchData doesn't unmount the success dialog.
    if (wasSuccess && onSubmitted) onSubmitted(orderNumber);
  };

  if (success) {
    const deadline = success.ship_deadline ? new Date(success.ship_deadline).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent
          data-testid="shipping-success-dialog"
          className="bg-slate-900 border-slate-700 max-w-sm mx-4"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Order Created
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Your shipping info has been sent to the host. Order confirmation emailed to <span className="text-emerald-300">{form.email}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <p className="text-[10px] text-emerald-200 uppercase tracking-wide">Order Number</p>
              <p data-testid="shipping-success-order" className="text-lg font-bold text-white font-mono">{success.order_number}</p>
            </div>
            <p className="text-[11px] text-slate-400">
              Host has until <span className="text-amber-300 font-semibold">{deadline}</span> to ship. You&apos;ll get a tracking email when they mark it shipped.
            </p>
            <Button data-testid="shipping-success-close" onClick={handleClose} className="w-full bg-orange-500 hover:bg-orange-600">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shipping-info-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-400" /> Shipping Address
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Provide your shipping details. The host will only see this once you submit.
          </DialogDescription>
        </DialogHeader>
        <p className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
          📦 All physical items ship signature-required. If a shipment goes out without a signature requirement, the sale is final.
        </p>
        <div className="space-y-2.5 pt-2">
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-2.5 flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 text-purple-300 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-purple-200 leading-snug">
              You won: <span className="text-white font-semibold">{escrow?.prize_description || 'a prize'}</span>
            </p>
          </div>
          <Input data-testid="shipping-name" placeholder="Full name *" value={form.full_name} onChange={update('full_name')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          <Input data-testid="shipping-address1" placeholder="Address line 1 *" value={form.address_line1} onChange={update('address_line1')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          <Input data-testid="shipping-address2" placeholder="Apartment, suite, unit (optional)" value={form.address_line2} onChange={update('address_line2')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="shipping-city" placeholder="City *" value={form.city} onChange={update('city')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Input data-testid="shipping-state" placeholder="State / Province" value={form.state} onChange={update('state')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="shipping-postal" placeholder="ZIP / Postal *" value={form.postal_code} onChange={update('postal_code')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Input data-testid="shipping-country" placeholder="Country *" value={form.country} onChange={update('country')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          </div>
          <Input data-testid="shipping-phone" placeholder="Phone (carrier may need)" value={form.phone} onChange={update('phone')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          <Input data-testid="shipping-email" type="email" placeholder="Email for order confirmation *" value={form.email} onChange={update('email')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          <Input data-testid="shipping-notes" placeholder="Delivery notes (optional)" value={form.notes} onChange={update('notes')} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
          <p className="text-[9px] text-slate-500 leading-snug">
            Buyer protection: host has 14 days to ship + add tracking. If they don&apos;t, your purchase is auto-refunded.
          </p>
          <Button data-testid="shipping-submit" onClick={handleSubmit} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 h-11 font-semibold">
            {saving ? 'Submitting...' : 'Confirm & Generate Order #'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShippingInfoDialog;
